import {Component} from '@angular/core';
import { EChartsOption } from "echarts";
import { filter } from "rxjs";


import { CommonUtilitiesService } from '../services/commonUtilities.service';


interface InvestmentItem {
  category: string;
  amount: number;
  percentage: number;
}

interface EChartsData {
  name: string;
  value: number;
}

export interface EChartsDomObject {
  key: number;
  value: EChartsOption;
  optionality?: any;
}

interface DataEntry {
  name: string;
  amount?: string | null;
}

interface DataRow {
  cells: string[];
}



export class chartFromTextParser {

  private static commonUtilitiesService = CommonUtilitiesService;

  //NB: These must match standard and <br> line breaks, \n
  //https://regex101.com/r/dSc3WZ/1
  private static readonly INVESTMENT_PATTERN =
    /(?:(?:(?:-\s*)?(?:<strong>([^<]+)<\/strong>|([^:<\n]+)):\s+(?:\$?([\d,]+\.?\d*)\s+)?\(?([-\d.]+)%\)?)|(?:-\s*([^:<\n]+):\s+([-\d.]+)%))(?:\s*(?:<br>|\n)+)?/gmi;

  //TODO: make sthe above also accept this MD pattern
  /**
   * Q:
   * Your portfolio is allocated as follows:

- Real Estate (RE): 64.32%
- Private Equity (PE): 24.66%
- Working Capital (WK): 14.06%
- Managed Assets (MA): 2.67%
- Cash Account (CA): -5.7%

[*] See the table for details.
   */

  // Pattern to detect if content contains a markdown table
  public static readonly TABLE_DETECTION_PATTERN = /\s*\|.*\|(?:\n|<br\/?>)\s*\|[-: |]+\|(?:\n|<br\/?>)(?:\s*\|.*\|(?:\n)?)+/gmi;
  // /^\s*\|.*\|\s*\n\s*\|[-\s|]*\|\s*\n/mgi;

  // Matches the entire table structure
  private static readonly TABLE_PATTERN = /\|.*\|.*\|.*\|\n\|[-\s|]*\n((?:\|.*\|.*\|.*\|\n?)*)/;

  // Matches individual rows (excluding header and separator rows)
  private static readonly ROW_PATTERN = /\|\s*(.*?)\s*\|\s*([\d.]+)\s*\|\s*([\d.]+)\s*\|/g;

  // Pattern to capture headers
  private static readonly HEADER_PATTERN = /^\s*\|(.*)\|\s*$/m;

  // Pattern to capture only data rows, excluding header and separator
  private static readonly DATA_ROWS_PATTERN =
    /\|.*\|\n\|[-|\s]+\|\n((?:\|.*\|\n*)*)/;

  // Pattern for splitting individual rows
  private static readonly ROW_SPLIT_PATTERN = /\|(.*?)\|/g;



  /**
   * Parses HTML content containing investment portfolio data
   * @param htmlContent The HTML string containing the investment data
   * @returns Array of parsed investment items
   */
  static parseInvestments(htmlContent: string): InvestmentItem[] {
    const investments: InvestmentItem[] = [];
    const matches = htmlContent.matchAll(this.INVESTMENT_PATTERN);

    for (const match of matches) {

      console.log(match);

      const [_, category, amountStr, percentageStr] = match;
      investments.push({
        category: category.trim(),
        amount: parseFloat(amountStr.replace(/,/g, '')),
        percentage: parseFloat(percentageStr)
      });
    }

    return investments;
  }


  /**
   * Detects if the content contains a markdown table structure
   * @param content The string content to check
   * @returns boolean indicating if a markdown table is detected
   */
  static hasMarkdownTable(htmlContent: string): boolean {
    const matches = htmlContent.match(this.TABLE_DETECTION_PATTERN);
    return matches !== null;
  }


  static detectMarkdownTable(content: string): boolean {
    // First check if basic table structure exists
    if (!this.TABLE_DETECTION_PATTERN.test(content)) {
      return false;
    }

    // Then verify we can extract at least one valid data row
    const tableMatch = content.match(this.TABLE_PATTERN);
    if (!tableMatch) {
      return false;
    }

    const tableContent = tableMatch[1];
    const rowMatches = Array.from(tableContent.matchAll(this.ROW_PATTERN));

    // Return true if we have at least one valid data row
    return rowMatches.length > 0;
  }


  static parseDataRows(markdownContent: string): string[][] {
    const match = markdownContent.match(this.DATA_ROWS_PATTERN);
    if (!match) return [];

    const dataRowsContent = match[1];
    return dataRowsContent
      .split('\n')
      .filter(row => row.trim()) // Remove empty rows
      .map(row => {
        // Extract cell values from each row
        const cells: string[] = [];
        const rowMatches = row.matchAll(this.ROW_SPLIT_PATTERN);
        for (const match of rowMatches) {
          if (match[1]) {
            cells.push(match[1].trim());
          }
        }
        return cells;
      });
  }

  /**
   * Parses a markdown table containing project performance data
   * @param markdownContent The markdown table string
   * @returns Array of parsed performance data items
   */
  static parsePerformanceTable(markdownContent: string): InvestmentItem[] {
    const performances: InvestmentItem[] = [];

    // First verify we have a valid table
    if (!this.hasMarkdownTable(markdownContent)) {
      console.log("((((( returning here....");
      return performances;
    }

    const tableMatch = markdownContent.match(this.TABLE_PATTERN);
    if (!tableMatch) {
      console.log("((((( returning here....NO JOY");
      return performances;
    }

    const tableContent = tableMatch[1];
    const matches = tableContent.matchAll(this.ROW_PATTERN);

    for (const match of matches) {
      const [_, projectName, performanceStr, returnStr] = match;
      performances.push({
        category: projectName.trim(),
        amount: parseFloat(performanceStr),
        percentage: parseFloat(returnStr)
      });
    }

    return performances;
  }

  private static getEmojiForCategory(name: string): string {
    if (!name || typeof name !== 'string') return '📊';
    const n = name.toLowerCase();
    if (n.includes('hospital') || n.includes('clinic')) return '🏥';
    if (n.includes('housing') || n.includes('real estate') || n.includes('estate')) return '🏠';
    if (n.includes('debt')) return '💳';
    if (n.includes('equity') || n.includes('private equity')) return '📈';
    if (n.includes('cash')) return '💵';
    if (n.includes('medical')) return '⚕️';
    return '📊';
  }

  private static getDisplayNameWithEmoji(name: string): string {
    const emoji = this.getEmojiForCategory(name);
    const cleanName = (name || '').replace(/\&amp;/g, '&').trim();
    return `${emoji} ${cleanName}`;
  }

  private static isLightChartColor(hex: string): boolean {
    const normalized = hex.replace('#', '');
    if (normalized.length !== 6) {
      return false;
    }

    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.65;
  }

  private static readonly CHART_COLORS = [
    '#E11931',
    '#2A4259',
    '#425166',
    '#6D7284',
    '#DDDEE0',
    '#F8B2AC',
  ];

  private static readonly SKIP_VALUE_HEADERS =
    /maturity|^\s*date\s*$|tenor|remaining|currency|rate|isin|account\s*number/i;

  private static readonly VALUE_COLUMN_RULES: Array<{
    test: RegExp;
    mode: 'amount' | 'percentage';
    priority: number;
  }> = [
    { test: /^balance$/i, mode: 'amount', priority: 1 },
    { test: /^amount\s*base$/i, mode: 'amount', priority: 2 },
    { test: /^amount$/i, mode: 'amount', priority: 3 },
    { test: /^nav$/i, mode: 'amount', priority: 4 },
    { test: /^value$/i, mode: 'amount', priority: 5 },
    { test: /allocation|percent|%/i, mode: 'percentage', priority: 6 },
    { test: /performance|return/i, mode: 'percentage', priority: 7 },
  ];

  private static parseMarkdownTable(
    markdownTable: string
  ): { headers: string[]; rows: Record<string, string>[] } | null {
    const lines = markdownTable.trim().split('\n').filter((line) => line.trim());
    if (lines.length < 3) {
      return null;
    }

    const headers = lines[0]
      .trim()
      .split('|')
      .filter((cell) => cell.trim())
      .map((header) => header.trim());

    if (headers.length <= 1) {
      return null;
    }

    const separatorLine = lines[1];
    if (!separatorLine.match(/^\|?([\s-:|]+\|)+[\s-:|]+\|?$/)) {
      return null;
    }

    const rows = lines.slice(2).map((line) => {
      const values = line
        .trim()
        .split('|')
        .filter((cell) => cell !== '')
        .map((cell) => cell.trim());

      return headers.reduce(
        (obj, header, index) => {
          obj[header] = values[index] || '';
          return obj;
        },
        {} as Record<string, string>
      );
    });

    return { headers, rows };
  }

  private static resolveNameColumn(headers: string[]): string {
    const preferred = headers.find((header) =>
      /^(name|project|project\s*name|component|sector|account|holding|description)$/i.test(
        header.trim()
      )
    );
    return preferred || headers[0];
  }

  private static isSummaryRow(label: string): boolean {
    const normalized = (label || '').trim().toLowerCase();
    return (
      normalized === 'total' ||
      normalized === 'grand total' ||
      normalized.includes('total portfolio') ||
      normalized === 'portfolio total' ||
      normalized === 'overall total'
    );
  }

  private static looksLikeDateValue(value: string): boolean {
    const trimmed = (value || '').trim();
    return (
      /^\d{4}-\d{2}-\d{2}/.test(trimmed) ||
      /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/.test(trimmed)
    );
  }

  private static parseNumericCell(value: string, mode: 'amount' | 'percentage'): number | null {
    if (!value || this.looksLikeDateValue(value)) {
      return null;
    }

    const parsed = this.commonUtilitiesService.convertStringToNumber(value);
    if (isNaN(parsed)) {
      return null;
    }

    if (mode === 'percentage' && Math.abs(parsed) > 1000) {
      return null;
    }

    return parsed;
  }

  private static resolveValueColumn(
    headers: string[],
    rows: Record<string, string>[]
  ): { column: string; mode: 'amount' | 'percentage' } | null {
    let bestMatch: { column: string; mode: 'amount' | 'percentage'; priority: number } | null =
      null;

    for (const header of headers) {
      if (this.SKIP_VALUE_HEADERS.test(header)) {
        continue;
      }

      for (const rule of this.VALUE_COLUMN_RULES) {
        if (rule.test.test(header) && (!bestMatch || rule.priority < bestMatch.priority)) {
          bestMatch = { column: header, mode: rule.mode, priority: rule.priority };
        }
      }
    }

    if (bestMatch) {
      return { column: bestMatch.column, mode: bestMatch.mode };
    }

    let inferredBest: { column: string; mode: 'amount' | 'percentage'; score: number } | null =
      null;

    for (const header of headers) {
      if (this.SKIP_VALUE_HEADERS.test(header)) {
        continue;
      }

      const numericValues = rows
        .map((row) => this.parseNumericCell(row[header], 'amount'))
        .filter((value): value is number => value !== null);

      if (numericValues.length < 2) {
        continue;
      }

      const hasPercentMarker = rows.some((row) => (row[header] || '').includes('%'));
      const sum = numericValues.reduce((total, value) => total + value, 0);
      const mode: 'amount' | 'percentage' =
        hasPercentMarker || (sum >= 90 && sum <= 110 && numericValues.every((v) => Math.abs(v) <= 100))
          ? 'percentage'
          : 'amount';

      const score = numericValues.length * (mode === 'amount' ? 2 : 1);
      if (!inferredBest || score > inferredBest.score) {
        inferredBest = { column: header, mode, score };
      }
    }

    return inferredBest ? { column: inferredBest.column, mode: inferredBest.mode } : null;
  }

  private static detectUniformCurrency(
    headers: string[],
    rows: Record<string, string>[]
  ): string | null {
    const currencyHeader = headers.find((header) => /^currency$/i.test(header.trim()));
    if (!currencyHeader) {
      return null;
    }

    const currencies = new Set(
      rows.map((row) => (row[currencyHeader] || '').trim()).filter(Boolean)
    );

    return currencies.size === 1 ? [...currencies][0] : null;
  }

  private static truncateToOneDecimal(value: number): number {
    return Math.trunc(value * 10) / 10;
  }

  private static truncateToTwoDecimals(value: number): number {
    return Math.trunc(value * 100) / 100;
  }

  private static formatCompactValue(value: number, suffix = ''): string {
    const truncated = this.truncateToTwoDecimals(value);
    const formatted = truncated.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${formatted}${suffix}`;
  }

  private static formatAmountCenterLabel(total: number): string {
    const absTotal = Math.abs(total);

    if (absTotal >= 1e6) {
      return this.formatCompactValue(total / 1e6, 'm');
    }
    if (absTotal >= 1e3) {
      return this.formatCompactValue(total / 1e3, 'k');
    }
    return this.formatCompactValue(total);
  }

  private static formatCenterLabel(
    total: number,
    mode: 'amount' | 'percentage',
    _currency?: string | null
  ): string {
    if (mode === 'percentage') {
      const truncated = this.truncateToOneDecimal(total);
      if (Number.isInteger(truncated)) {
        return `${truncated}%`;
      }
      return `${truncated.toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;
    }

    return this.formatAmountCenterLabel(total);
  }

  /**
   * Transforms markdown table or legacy row objects into eCharts configuration.
   */
  static generateEChartsConfig(input: string | any[]): EChartsOption | null {
    if (typeof input === 'string') {
      return this.generateEChartsConfigFromMarkdown(input);
    }

    return this.buildEChartsConfig(
      input.map((item) => ({
        name: item.name || '',
        value: this.commonUtilitiesService.convertStringToNumber(item.value),
      })),
      'amount',
      null
    );
  }

  private static generateEChartsConfigFromMarkdown(markdownTable: string): EChartsOption | null {
    const parsed = this.parseMarkdownTable(markdownTable);
    if (!parsed) {
      return null;
    }

    const { headers, rows } = parsed;
    const nameColumn = this.resolveNameColumn(headers);
    const valueMeta = this.resolveValueColumn(headers, rows);
    if (!valueMeta) {
      return null;
    }

    const dataRows = rows.filter((row) => !this.isSummaryRow(row[nameColumn] || ''));
    if (dataRows.length < 2) {
      return null;
    }

    const currency = this.detectUniformCurrency(headers, dataRows);
    const chartRows = dataRows
      .map((row) => {
        const rawName = (row[nameColumn] || '').replace(/\&amp;/g, '&');
        const numericValue = this.parseNumericCell(row[valueMeta.column], valueMeta.mode);
        if (numericValue === null) {
          return null;
        }

      return {
          name: this.getDisplayNameWithEmoji(rawName),
          value: numericValue,
        };
      })
      .filter((item): item is EChartsData => item !== null);

    return this.buildEChartsConfig(chartRows, valueMeta.mode, currency);
  }

  private static buildEChartsConfig(
    chartData: EChartsData[],
    mode: 'amount' | 'percentage',
    currency: string | null
  ): EChartsOption | null {
    const filteredData = chartData.filter((item) => !isNaN(Number(item.value)));

    if (filteredData.length < 2) {
      return null;
    }

    const totalValue = filteredData.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
    const totalStr = this.formatCenterLabel(totalValue, mode, currency);

    const textPrimary = '#393939';
    const seriesData = filteredData.map((item, index) => {
      const sliceColor = this.CHART_COLORS[index % this.CHART_COLORS.length];
      const labelColor = this.isLightChartColor(sliceColor) ? textPrimary : '#ffffff';

      return {
        ...item,
        label: { color: labelColor },
      };
    });

    let objPreReturn: EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        confine: true,
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
        borderColor: 'rgba(42, 66, 89, 0.2)',
        borderWidth: 1,
        textStyle: { color: textPrimary, fontSize: 12 },
        formatter: '{b}: {c} ({d}%)',
      },
      color: this.CHART_COLORS,
      series: [
        {
          name: 'Portfolio',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          minAngle: 8,
          percentPrecision: 0,
          data: seriesData,
          labelLine: { show: false },
          label: {
            show: true,
            position: 'inside',
            color: '#ffffff',
            fontSize: 11,
            fontWeight: 'bold',
            formatter: '{d}%',
          },
          itemStyle: {
            borderRadius: 4,
            borderColor: 'rgba(0, 0, 0, 0.2)',
            borderWidth: 1,
            shadowBlur: 8,
            shadowOffsetY: 2,
            shadowColor: 'rgba(0, 35, 51, 0.15)',
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 14,
              shadowOffsetY: 4,
              shadowColor: 'rgba(0, 35, 51, 0.25)',
              borderWidth: 2,
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            label: { show: true, fontWeight: 'bold' },
          },
        },
        {
          name: '__center__',
          type: 'pie',
          radius: ['0%', '38%'],
          center: ['50%', '50%'],
          silent: true,
          z: 10,
          data: [{ value: 1, name: '' }],
          itemStyle: { color: 'transparent', borderWidth: 0 },
          label: {
            show: true,
            position: 'center',
            formatter: totalStr,
            fontSize: 24,
            fontWeight: 'bold',
            color: '#ffffff',
          },
          emphasis: { scale: false, label: { show: true } },
        },
      ]
    };

    return objPreReturn;
  }

  /**
   * Validates if the HTML content contains multiple investment items
   * @param htmlContent The HTML string to validate
   * @returns boolean indicating if multiple investments are present
   */
  static hasMultipleInvestments(htmlContent: string): boolean {
    const matches = htmlContent.match(this.INVESTMENT_PATTERN);
    return matches !== null;
  }

  /**
   * Calculates the total portfolio value
   * @param investments Array of investment items
   * @returns Total portfolio value
   */
  static calculateTotalPortfolio(investments: InvestmentItem[]): number {
    return investments.reduce((sum, item) => sum + item.amount, 0);
  }



} //end class

